// ─── FileStorage.js ───────────────────────────────────────────────────────────
// File System Access API utilities for crew-portal.
// Files are stored in a user-selected local folder:
//   {root}/freelancers/{id}/passport/
//   {root}/freelancers/{id}/photo/
//   {root}/freelancers/{id}/cv/
//   {root}/freelancers/{id}/visa-{visaId}/
//   {root}/freelancers/{id}/other-{docId}/

const DB_NAME  = 'crew-portal';
const DB_VER   = 1;
const STORE    = 'handles';
const ROOT_KEY = 'rootDir';

// ─── IndexedDB (store the directory handle between sessions) ──────────────────

function openDB() {
  return new Promise((res, rej) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = e => e.target.result.createObjectStore(STORE);
    req.onsuccess = e => res(e.target.result);
    req.onerror   = () => rej(req.error);
  });
}

export async function saveHandle(handle) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(handle, ROOT_KEY);
    tx.oncomplete = res;
    tx.onerror    = () => rej(tx.error);
  });
}

export async function loadHandle() {
  try {
    const db = await openDB();
    return new Promise((res, rej) => {
      const tx  = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(ROOT_KEY);
      req.onsuccess = () => res(req.result ?? null);
      req.onerror   = () => rej(req.error);
    });
  } catch { return null; }
}

// ─── Directory selection & permission ────────────────────────────────────────

export async function selectDirectory() {
  try {
    const h = await window.showDirectoryPicker({ mode: 'readwrite', id: 'crew-portal-files' });
    await saveHandle(h);
    return h;
  } catch { return null; }
}

export async function queryPermission(handle) {
  try { return await handle.queryPermission({ mode: 'readwrite' }); }
  catch { return 'denied'; }
}

export async function requestPermission(handle) {
  try {
    const p = await handle.requestPermission({ mode: 'readwrite' });
    return p === 'granted';
  } catch { return false; }
}

// ─── Low-level dir/file helpers ───────────────────────────────────────────────

async function getDir(root, parts, create = false) {
  let cur = root;
  for (const p of parts) cur = await cur.getDirectoryHandle(p, { create });
  return cur;
}

export async function writeFile(rootHandle, parts, rawFile) {
  const dir = await getDir(rootHandle, parts.slice(0, -1), true);
  const fh  = await dir.getFileHandle(parts.at(-1), { create: true });
  const wr  = await fh.createWritable();
  await wr.write(rawFile);
  await wr.close();
}

export async function deleteFile(rootHandle, parts) {
  try {
    const dir = await getDir(rootHandle, parts.slice(0, -1));
    await dir.removeEntry(parts.at(-1));
  } catch {}
}

export async function listDir(rootHandle, parts) {
  try {
    const dir   = await getDir(rootHandle, parts);
    const items = [];
    for await (const [name, handle] of dir.entries()) {
      if (handle.kind === 'file') {
        const f = await handle.getFile();
        items.push({ id: name, name, size: f.size, type: f.type, url: URL.createObjectURL(f) });
      }
    }
    return items;
  } catch { return []; }
}

// ─── High-level: freelancer docs ─────────────────────────────────────────────

export async function loadFreelancerDocs(rootHandle, freelancerId) {
  const base = ['freelancers', String(freelancerId)];
  const [passportFiles, photoFiles, cvFiles] = await Promise.all([
    listDir(rootHandle, [...base, 'passport']),
    listDir(rootHandle, [...base, 'photo']),
    listDir(rootHandle, [...base, 'cv']),
  ]);
  return { passportFiles, photoFiles, cvFiles };
}

// Write any file objects that have _raw (newly uploaded, not yet on disk).
export async function saveFreelancerDocs(rootHandle, freelancerId, data) {
  const id   = String(freelancerId);
  const base = ['freelancers', id];

  const writeAll = async (files, subDir) => {
    for (const f of files || []) {
      if (f._raw) await writeFile(rootHandle, [...base, subDir, f.name], f._raw);
    }
  };

  await writeAll(data.passportFiles, 'passport');
  await writeAll(data.photoFiles,    'photo');
  await writeAll(data.cvFiles,       'cv');
  for (const v of data.visas     || []) await writeAll(v.files,  `visa-${v.id}`);
  for (const d of data.otherDocs || []) await writeAll(d.files, `other-${d.id}`);
}

// Delete files that were removed during editing.
// prevFiles / nextFiles are arrays of { id, name }
export async function deleteRemovedFiles(rootHandle, freelancerId, subDir, prevFiles, nextFiles) {
  const base     = ['freelancers', String(freelancerId)];
  const nextIds  = new Set((nextFiles || []).map(f => f.id));
  for (const f of prevFiles || []) {
    if (!nextIds.has(f.id)) {
      await deleteFile(rootHandle, [...base, subDir, f.name]);
    }
  }
}
